/**
 * LabelPillView — neutral rounded-full label pill with a closed tone enum
 * (SE-5527). Replaces inline pill markup in feature Views (WatchlistSummary
 * last-action pill, NotesSection count pill, PlayerFinancialsTab "Soon"
 * badge) so no feature spells pill chrome by hand. Semantic tokens only;
 * pairings live in one closed map (Badge `TONE_CLASS` style).
 * @module components/molecules/LabelPill/LabelPillView
 */
import React, { memo } from "react";

import { Box } from "@/components/atoms/Box";
import { Text } from "@/components/atoms/Text";

/** Tone → box/text class pairing (closed). */
const TONE_CLASS: Record<
  "subtle" | "muted",
  { readonly box: string; readonly text: string }
> = {
  subtle: { box: "bg-surface-subtle", text: "text-content-muted" },
  muted: {
    box: "bg-surface-muted",
    text: "font-medium text-content-secondary",
  },
};

/** Props for LabelPillView. */
export interface LabelPillProps {
  /** Pill label. */
  readonly label: string;
  /** Neutral tone pairing. */
  readonly tone: "subtle" | "muted";
  /** Optional test identity. */
  readonly testID?: string;
}

/**
 * Renders a neutral label pill.
 * @param root0 - Component props
 * @param root0.label - Pill label
 * @param root0.tone - Neutral tone pairing
 * @param root0.testID - Optional test identity
 * @returns The pill element.
 */
const LabelPillView = ({ label, tone, testID }: LabelPillProps) => (
  <Box
    testID={testID}
    className={`rounded-full px-2 py-micro ${TONE_CLASS[tone].box}`}
  >
    <Text variant="caption" className={TONE_CLASS[tone].text}>
      {label}
    </Text>
  </Box>
);

LabelPillView.displayName = "LabelPillView";

export default memo(LabelPillView);
